import React, { useState, useEffect, Fragment } from 'react';
import { Modal, Button, Row, Col, Form, Card, Badge, ProgressBar, Alert, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../../../styles/ai-forward-theme.css';
import { 
  faUser, 
  faEnvelope, 
  faPhone, 
  faHome, 
  faMapMarker, 
  faMap, 
  faMailBulk, 
  faBullseye, 
  faTasks, 
  faTags, 
  faComment, 
  faTimes,
  faCheckCircle,
  faLightbulb,
  faRobot,
  faSave,
  faArrowRight,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import KairosLogoOnly from '../../../assets/img/logos/KairosIQ-LogoOnly.png';

interface FieldMappingModalProps {
  show: boolean;
  onHide: () => void;
  detectedColumns: string[];
  sourceSystem: string;
  aiSuggestions: Record<string, string>;
  onConfirmMapping: (mappings: Record<string, string>) => void;
  sampleData?: string[][];
}

const TARGET_FIELDS = [
  // Skip Column (First for convenience)
  { value: 'ignore', label: 'Ignore This Column', icon: faTimes },
  
  // Primary Contact Fields
  { value: 'lead_first_name', label: 'First Name', icon: faUser },
  { value: 'lead_last_name', label: 'Last Name', icon: faUser },
  { value: 'lead_email', label: 'Primary Email', icon: faEnvelope },
  { value: 'lead_alternative_email', label: 'Alternative Email', icon: faEnvelope },
  { value: 'lead_phone', label: 'Phone Number', icon: faPhone },
  
  // Address Fields
  { value: 'lead_street_name', label: 'Street Address', icon: faHome },
  { value: 'lead_city', label: 'City', icon: faMapMarker },
  { value: 'lead_state', label: 'State/Province', icon: faMap },
  { value: 'lead_zip_code', label: 'ZIP/Postal Code', icon: faMailBulk },
  
  // Business Information
  { value: 'lead_company', label: 'Company Name', icon: faHome },
  { value: 'lead_title', label: 'Job Title', icon: faBullseye },
  
  // Lead Information
  { value: 'lead_source', label: 'Lead Source', icon: faBullseye },
  { value: 'lead_type', label: 'Lead Type (Buyer/Seller)', icon: faBullseye },
  { value: 'lead_status', label: 'Lead Status', icon: faTasks },
  { value: 'lead_score', label: 'Lead Score', icon: faTasks },
  { value: 'lead_description', label: 'Notes/Description', icon: faComment },
  { value: 'priority_level', label: 'Priority Level', icon: faTasks },
  
  // Assignment & Ownership
  { value: 'assigned_to', label: 'Assigned To', icon: faUser },
  { value: 'lead_owner', label: 'Lead Owner', icon: faUser },
  
  // Important Dates
  { value: 'date_added', label: 'Date Added', icon: faTasks },
  { value: 'last_activity', label: 'Last Activity Date', icon: faTasks },
  { value: 'follow_up_date', label: 'Follow Up Date', icon: faTasks },
  
  // Property Interest
  { value: 'property_type', label: 'Property Type Interest', icon: faHome },
  { value: 'price_range', label: 'Price Range', icon: faTasks },
  { value: 'bedrooms', label: 'Bedrooms Desired', icon: faHome },
  { value: 'bathrooms', label: 'Bathrooms Desired', icon: faHome },
  { value: 'square_feet', label: 'Square Feet Desired', icon: faHome },
  
  // Buyer Profile Fields
  { value: 'buyer_type', label: 'Buyer Type (First-time/Investor)', icon: faUser },
  { value: 'pre_approval_status', label: 'Pre-Approval Status', icon: faTasks },
  { value: 'pre_approval_amount', label: 'Pre-Approved Amount', icon: faTasks },
  { value: 'down_payment_amount', label: 'Down Payment Available', icon: faTasks },
  { value: 'desired_move_date', label: 'Desired Move Date', icon: faTasks },
  { value: 'preferred_locations', label: 'Preferred Locations', icon: faMapMarker },
  { value: 'must_have_features', label: 'Must Have Features', icon: faTasks },
  { value: 'nice_to_have_features', label: 'Nice to Have Features', icon: faTasks },
  { value: 'deal_breakers', label: 'Deal Breakers', icon: faTimes },
  { value: 'school_requirements', label: 'School Requirements', icon: faTasks },
  { value: 'commute_requirements', label: 'Commute Requirements', icon: faMap },
  
  // Seller Profile Fields
  { value: 'property_address', label: 'Property Address (Selling)', icon: faHome },
  { value: 'property_type_selling', label: 'Property Type (Selling)', icon: faHome },
  { value: 'listing_price', label: 'Desired Listing Price', icon: faTasks },
  { value: 'property_condition', label: 'Property Condition', icon: faHome },
  { value: 'bedrooms_current', label: 'Current Bedrooms', icon: faHome },
  { value: 'bathrooms_current', label: 'Current Bathrooms', icon: faHome },
  { value: 'square_feet_current', label: 'Current Square Feet', icon: faHome },
  { value: 'lot_size', label: 'Lot Size', icon: faHome },
  { value: 'year_built', label: 'Year Built', icon: faHome },
  { value: 'renovations', label: 'Recent Renovations', icon: faHome },
  { value: 'reason_for_selling', label: 'Reason for Selling', icon: faComment },
  { value: 'timeline_to_sell', label: 'Selling Timeline', icon: faTasks },
  { value: 'mortgage_balance', label: 'Mortgage Balance', icon: faTasks },
  { value: 'property_tax', label: 'Annual Property Tax', icon: faTasks },
  { value: 'hoa_fees', label: 'HOA Fees', icon: faTasks },
  
  // Additional Fields
  { value: 'description', label: 'Description/Notes', icon: faComment },
  { value: 'tags', label: 'Tags (comma-separated)', icon: faTags },
  { value: 'custom_field_1', label: 'Custom Field 1', icon: faTasks },
  { value: 'custom_field_2', label: 'Custom Field 2', icon: faTasks },
  { value: 'custom_field_3', label: 'Custom Field 3', icon: faTasks }
];

const FieldMappingModal: React.FC<FieldMappingModalProps> = ({
  show,
  onHide,
  detectedColumns,
  sourceSystem,
  aiSuggestions,
  onConfirmMapping,
  sampleData = []
}) => {
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    // Initialize mappings with AI suggestions, defaulting to 'ignore' for unmapped columns
    if (detectedColumns.length > 0) {
      const initialMappings: Record<string, string> = {};
      detectedColumns.forEach(col => {
        // Use AI suggestion if available, otherwise default to 'ignore'
        initialMappings[col] = aiSuggestions[col] || 'ignore';
      });
      setMappings(initialMappings);
      // Validate initial mappings
      validateMappings(initialMappings);
    }
  }, [detectedColumns, aiSuggestions]);

  const handleMappingChange = (sourceCol: string, targetField: string) => {
    const newMappings = {
      ...mappings,
      [sourceCol]: targetField
    };
    setMappings(newMappings);
    validateMappings(newMappings);
  };

  const validateMappings = (mappingsToValidate = mappings) => {
    const errors: string[] = [];
    const mappedTargets = Object.values(mappingsToValidate).filter(v => v !== 'ignore');
    
    // Check for required fields - using database field names
    const requiredFields = ['lead_email', 'lead_phone'];
    const hasRequired = requiredFields.some(field => mappedTargets.includes(field));
    if (!hasRequired) {
      errors.push('At least one contact method (email or phone) must be mapped');
    }

    // Check for duplicate mappings
    const targetCounts: Record<string, string[]> = {};
    Object.entries(mappingsToValidate).forEach(([sourceCol, targetField]) => {
      if (targetField !== 'ignore') {
        if (!targetCounts[targetField]) {
          targetCounts[targetField] = [];
        }
        targetCounts[targetField].push(sourceCol);
      }
    });

    const duplicateFields: string[] = [];
    Object.entries(targetCounts).forEach(([targetField, sourceCols]) => {
      if (sourceCols.length > 1) {
        const targetLabel = TARGET_FIELDS.find(f => f.value === targetField)?.label || targetField;
        duplicateFields.push(`"${targetLabel}" is mapped from: ${sourceCols.join(', ')}`);
      }
    });

    if (duplicateFields.length > 0) {
      errors.push(`Multiple columns cannot map to the same field:\n${duplicateFields.join('\n')}`);
    }

    console.log('[VALIDATION] Errors:', errors);
    console.log('[VALIDATION] Duplicate fields:', duplicateFields);
    setValidationErrors(errors);
  };

  const getMappingConfidence = (sourceCol: string) => {
    if (aiSuggestions[sourceCol] && mappings[sourceCol] === aiSuggestions[sourceCol]) {
      return 'high';
    }
    if (aiSuggestions[sourceCol]) {
      return 'medium';
    }
    return 'low';
  };

  const handleConfirm = () => {
    validateMappings();
    if (validationErrors.length === 0) {
      onConfirmMapping(mappings);
    }
  };

  const completedMappings = Object.values(mappings).filter(v => v !== 'ignore').length;
  const mappingProgress = (completedMappings / detectedColumns.length) * 100;

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" className="ai-modal">
      <Modal.Header closeButton className="ai-modal-header">
        <Modal.Title className="d-flex align-items-center">
          <div className="ai-icon-wrapper me-3">
            <img 
              src={KairosLogoOnly} 
              alt="Kairos AI" 
              style={{ 
                width: '28px', 
                height: '28px', 
                filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))'
              }} 
            />
          </div>
          <div>
            <h4 className="mb-0">AI-Powered Field Mapping</h4>
            <small className="text-muted">Neural Intelligence Active</small>
          </div>
          <Badge className="ai-status-badge ms-auto">
            <span className="ai-pulse-dot"></span>
            AI ANALYZING
          </Badge>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="ai-modal-body">
        <Alert className="ai-alert ai-alert-info mb-4">
          <FontAwesomeIcon icon={faLightbulb} className="me-2 ai-glow-icon" />
          <strong>Neural Intelligence Active:</strong> Our AI has analyzed your {sourceSystem} file and detected optimal field mappings with {Math.round(Object.keys(aiSuggestions).length / detectedColumns.length * 100)}% confidence.
        </Alert>

        <div className="ai-progress-container">
          <div className="ai-progress-header">
            <span className="ai-progress-label">Mapping Progress</span>
            <span className="ai-progress-value">
              {completedMappings} / {detectedColumns.length}
            </span>
          </div>
          <div className="ai-progress-bar">
            <div className="ai-progress-fill" style={{ width: `${mappingProgress}%` }}>
              <span className="ai-progress-text">{Math.round(mappingProgress)}%</span>
            </div>
          </div>
        </div>

        {validationErrors.length > 0 && (
          <Alert variant="warning" className="mb-3">
            {validationErrors.map((error, idx) => {
              // Split error message by newlines to properly display multi-line messages
              const lines = error.split('\n');
              return (
                <div key={idx}>
                  {lines.map((line, lineIdx) => (
                    <div key={`${idx}-${lineIdx}`}>
                      {lineIdx === 0 ? '• ' : '  '}{line}
                    </div>
                  ))}
                </div>
              );
            })}
          </Alert>
        )}

        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Visual Data Preview & Field Mapping
          </h6>
          
          <div className="ai-glass-card p-0">
            <div className="table-responsive">
              <Table className="ai-mapping-table mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0 py-3 px-3" style={{ width: '20%' }}>
                        <strong>Source Column</strong>
                      </th>
                      <th className="border-0 py-3 px-3" style={{ width: '35%' }}>
                        <strong>Sample Data</strong>
                        <small className="d-block text-muted mt-1">
                          Preview of your data (first 3 rows)
                        </small>
                      </th>
                      <th className="border-0 py-3 px-3 text-center" style={{ width: '10%' }}>
                        <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
                      </th>
                      <th className="border-0 py-3 px-3" style={{ width: '35%' }}>
                        <strong>Maps To Target Field</strong>
                        <small className="d-block text-muted mt-1">
                          Select the destination field
                        </small>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detectedColumns.map((col, colIndex) => {
                      const confidence = getMappingConfidence(col);
                      const targetField = TARGET_FIELDS.find(f => f.value === mappings[col]);
                      
                      return (
                        <tr key={col} className={`border-start border-3 border-${confidence === 'high' ? 'success' : confidence === 'medium' ? 'warning' : 'secondary'}`}>
                          <td className="py-3 px-3">
                            <div className="d-flex flex-column">
                              <strong className="text-primary mb-1">{col}</strong>
                              {aiSuggestions[col] && (
                                <Badge 
                                  className={`ai-status-badge align-self-start ${confidence === 'high' ? 'ai-confidence-high' : confidence === 'medium' ? 'ai-confidence-medium' : 'ai-confidence-low'}`}
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  <img 
                                    src={KairosLogoOnly} 
                                    alt="AI" 
                                    className="me-1"
                                    style={{ 
                                      width: '16px', 
                                      height: '16px', 
                                      verticalAlign: 'text-top'
                                    }} 
                                  />
                                  AI: {confidence === 'high' ? '95%' : confidence === 'medium' ? '75%' : '45%'} Match
                                </Badge>
                              )}
                            </div>
                          </td>
                          
                          <td className="py-3 px-3">
                            <div className="d-flex flex-wrap gap-1 align-items-center">
                              {sampleData.slice(0, 3).map((row, rowIndex) => (
                                <React.Fragment key={`sample-${colIndex}-${rowIndex}`}>
                                  <span 
                                    className="ai-sample-badge"
                                    style={{ 
                                      fontFamily: 'Monaco, Consolas, monospace',
                                      fontSize: '0.75rem',
                                      fontWeight: 'normal',
                                      maxWidth: '150px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'inline-block'
                                    }}
                                    title={row[colIndex] || 'Empty'}
                                  >
                                    {row[colIndex] || <span className="text-muted fst-italic">empty</span>}
                                  </span>
                                  {rowIndex < 2 && rowIndex < sampleData.length - 1 && (
                                    <span className="ai-sample-separator">|</span>
                                  )}
                                </React.Fragment>
                              ))}
                              {sampleData.length === 0 && (
                                <span className="text-muted small">No sample data available</span>
                              )}
                            </div>
                          </td>
                          
                          <td className="py-3 px-3 text-center">
                            <FontAwesomeIcon 
                              icon={mappings[col] !== 'ignore' && mappings[col] ? faArrowRight : faTimes}
                              className={`fs-4 ${
                                mappings[col] !== 'ignore' && mappings[col] 
                                  ? confidence === 'high' ? 'text-success' : 'text-primary' 
                                  : 'text-muted'
                              }`}
                            />
                          </td>
                          
                          <td className="py-3 px-3">
                            <Form.Select
                              value={mappings[col] || 'ignore'}
                              onChange={(e) => handleMappingChange(col, e.target.value)}
                              className={`ai-form-select ${aiSuggestions[col] === mappings[col] ? 'border-success' : ''}`}
                              size="sm"
                            >
                              {TARGET_FIELDS.map(field => (
                                <option key={field.value} value={field.value}>
                                  {field.label}
                                </option>
                              ))}
                            </Form.Select>
                            {targetField && mappings[col] !== 'ignore' && (
                              <div className="mt-2 d-flex align-items-center">
                                <FontAwesomeIcon icon={targetField.icon} className="me-2 text-success" />
                                <small className="text-success fw-medium">
                                  Mapping to: {targetField.label}
                                </small>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
        </div>

        <div className="mt-4">
          <Form.Check
            type="checkbox"
            id="save-template"
            label={`Save this mapping as a template for future ${sourceSystem} imports`}
            checked={saveAsTemplate}
            onChange={(e) => setSaveAsTemplate(e.target.checked)}
          />
        </div>
      </Modal.Body>
      
      <Modal.Footer className="ai-modal-footer">
        <Button variant="secondary" onClick={onHide} className="me-2">
          Cancel
        </Button>
        <Button 
          className="btn-ai-primary"
          onClick={handleConfirm}
          disabled={validationErrors.length > 0}
        >
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Deploy AI Mapping
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FieldMappingModal;